import { mockDataUtil } from './mockDataUtil';
import { itemMasterDataList } from '../mock/itemMasterDataMock';
import { API_ENDPOINTS } from '../utils/apiEndpoints';
import httpClient from './httpClient';
import { PaginatedResponse, ApiResponse } from '../types'; // 从index.ts导入分页响应类型
import { WHOLE_USE_MOCK } from './api';

// 模拟网络延迟
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

import {
  ItemMasterData,
  ItemMasterDataFilters,
  ItemMasterDataPaginationParams,
  ItemMasterDataPaginatedResponse,
  BrandOption,
  CategoryOption
} from '../types/item-master-data';

/**
 * Item Master Data Service
 * 货品主数据服务类，提供货品主数据相关的API调用
 */
class ItemMasterDataService {
  /**
   * 获取货品主数据分页列表
   * @param filters 筛选条件
   * @param pagination 分页参数
   * @returns Promise<ApiResponse<PaginatedResponse<ItemMasterData>>>
   */
  async getItemMasterDataPage(
    filters: ItemMasterDataFilters = {},
    pagination: ItemMasterDataPaginationParams
  ): Promise<ApiResponse<PaginatedResponse<ItemMasterData>>> {
    try {
      // 在开发环境使用Mock数据
      if ( WHOLE_USE_MOCK) {
        return this.getMockItemMasterDataPage(filters, pagination);
      }

      // 生产环境调用真实API
      const params = {
        ...filters,
        currentPage: pagination.currentPage,
        pageSize: pagination.pageSize,
        sortBy: pagination.sortBy,
        sortOrder: pagination.sortOrder
      };

      const response = await httpClient.post(API_ENDPOINTS.ITEM.PAGE, params);
      return response.data;
    } catch (error) {
      console.error('获取货品主数据列表失败:', error);
      return {
        traceId: 'error-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'FETCH_ERROR',
        errorMessage: '获取货品主数据列表失败，请稍后重试',
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * 获取货品主数据详情
   * @param itemCode 商品代码
   * @returns Promise<ApiResponse<ItemMasterData>>
   */
  async getItemMasterDataDetail(itemCode: string): Promise<ApiResponse<ItemMasterData>> {
    try {
      // 在开发环境使用Mock数据
      if ( WHOLE_USE_MOCK) {
        return this.getMockItemMasterDataDetail(itemCode);
      }

      // 生产环境调用真实API
      const response = await httpClient.get(API_ENDPOINTS.ITEM.DETAIL + "?code=" + itemCode);
      return response.data;
    } catch (error) {
      console.error('获取货品主数据详情失败:', error);
      return {
        traceId: 'error-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'FETCH_ERROR',
        errorMessage: '获取货品主数据详情失败，请稍后重试',
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * 获取品牌选项列表
   * @returns Promise<ApiResponse<BrandOption[]>>
   */
  async getBrandOptions(): Promise<ApiResponse<BrandOption[]>> {
    try {
      // 在开发环境使用Mock数据
      if ( WHOLE_USE_MOCK) {
        return this.getMockBrandOptions();
      }

      // 生产环境调用真实API
      const response = await httpClient.get(API_ENDPOINTS.ITEM.BRAND_LIST);
      return response.data;
    } catch (error) {
      console.error('获取品牌选项失败:', error);
      return {
        traceId: 'error-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'FETCH_ERROR',
        errorMessage: '获取品牌选项失败，请稍后重试',
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * 获取类目选项列表
   * @param level 类目层级
   * @param parentId 父类目ID
   * @returns Promise<ApiResponse<CategoryOption[]>>
   */
  async getCategoryOptions(level?: number, parentId?: string): Promise<ApiResponse<CategoryOption[]>> {
    try {
      // 在开发环境使用Mock数据
      if ( WHOLE_USE_MOCK) {
        return this.getMockCategoryOptions(level, parentId);
      }

      // 生产环境调用真实API
      const params: any = {};
      if (level !== undefined) params.level = level;
      if (parentId) params.parentId = parentId;

      const response = await httpClient.get(API_ENDPOINTS.CATEGORY.LIST, { params });
      return response.data;
    } catch (error) {
      console.error('获取类目选项失败:', error);
      return {
        traceId: 'error-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'FETCH_ERROR',
        errorMessage: '获取类目选项失败，请稍后重试',
        flag: 'fail',
        data: null
      };
    }
  }

  

  /**
   * 保存商品陈列参数配置
   * @param itemCode 商品代码
   * @param config 陈列参数配置
   * @returns Promise<ApiResponse<boolean>>
   */
  async saveDisplayConfig(itemCode: string, config: ItemMasterData): Promise<ApiResponse<boolean>> {
    try {
      // 在开发环境使用Mock数据
         if ( WHOLE_USE_MOCK) {
        return this.getMockSaveDisplayConfig(itemCode, config);
      }

      // 生产环境调用真实API
      const response = await httpClient.post(`${API_ENDPOINTS.ITEM.DISPLAY_CONFIG}`, {
        itemCode,
        ...config
      });
      return response.data;
    } catch (error) {
      console.error('保存陈列参数配置失败:', error);
      return {
        traceId: 'error-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'SAVE_ERROR',
        errorMessage: '保存陈列参数配置失败，请稍后重试',
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * 导出商品数据（别名方法）
   * @param filters 筛选条件
   * @returns Promise<Blob>
   */
  async exportItemData(filters: ItemMasterDataFilters = {}): Promise<any> {
    try {
      // 在开发环境返回模拟数据
      if (WHOLE_USE_MOCK) {
        return {
          success: true,
          data: {
            downloadUrl: '/mock/item-master-data.csv',
            fileName: '商品主数据.csv'
          }
        };
      }

      // 生产环境调用真实API
      const response = await httpClient.post(API_ENDPOINTS.ITEM.EXPORT, {
        ...filters
      });

      return response.data;
    } catch (error) {
      console.error('导出商品数据失败:', error);
      throw new Error('导出失败');
    }
  }

  /**
   * 导入陈列参数配置
   * @param file 导入文件
   * @returns Promise<ApiResponse<any>>
   */
  async importDisplayConfig(file: File): Promise<ApiResponse<any>> {
    try {
      // 在开发环境使用Mock数据
      if (WHOLE_USE_MOCK) {
        return this.getMockImportDisplayConfig(file);
      }

      // 生产环境调用真实API
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await httpClient.post(API_ENDPOINTS.ITEM.IMPORT_DISPLAY_CONFIG, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data;
    } catch (error) {
      console.error('导入陈列参数配置失败:', error);
      return {
        traceId: 'error-' + Date.now(),
        success: false,
        msg: null,
        errorCode: error.errorCode,
        errorMessage: error.measge,
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * 生成模拟CSV数据
   */
  private generateMockCSVData(filters: ItemMasterDataFilters): string {
    let filteredData = [...itemMasterDataList];
    
    // 应用筛选
    if (filters.itemCode) {
      filteredData = filteredData.filter(item => 
        item.code.toLowerCase().includes(filters.itemCode!.toLowerCase())
      );
    }
    if (filters.itemName) {
      filteredData = filteredData.filter(item => 
        item.name.toLowerCase().includes(filters.itemName!.toLowerCase())
      );
    }
    
    // 生成CSV头部
    const headers = [
      '货主编码',
      '商品编码',
      '商品名称',
      '英文名称',
      '商品简称',
      '库存单位',
      '商品类型',
      '状态'
    ];
    
    const rows = filteredData.map(item => [
      item.ownerCode,
      item.code,
      item.name,
      item.englishName || '',
      item.abbr || '',
      item.stockUnit,
      item.itemType,
      item.status
    ]);
    
    // 转换为CSV格式
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    return csvContent;
  }

  /**
   * Mock: 获取货品主数据分页列表
   */
  private async getMockItemMasterDataPage(
    filters: ItemMasterDataFilters,
    pagination: ItemMasterDataPaginationParams
  ): Promise<ApiResponse<ItemMasterDataPaginatedResponse>> {
    await delay(500);

    let filteredItems = [...itemMasterDataList];

    // 应用筛选条件
    if (filters.itemCode) {
      filteredItems = filteredItems.filter(item => 
        item.code.toLowerCase().includes(filters.itemCode!.toLowerCase())
      );
    }

    if (filters.itemName) {
      filteredItems = filteredItems.filter(item => 
        item.name.toLowerCase().includes(filters.itemName!.toLowerCase())
      );
    }

    if (filters.englishName) {
      filteredItems = filteredItems.filter(item => 
        item.englishName?.toLowerCase().includes(filters.englishName!.toLowerCase())
      );
    }

    if (filters.abbr) {
      filteredItems = filteredItems.filter(item => 
        item.abbr?.toLowerCase().includes(filters.abbr!.toLowerCase())
      );
    }

    if (filters.stockUnit) {
      filteredItems = filteredItems.filter(item => item.stockUnit === filters.stockUnit);
    }

    if (filters.itemType) {
      filteredItems = filteredItems.filter(item => item.itemType === filters.itemType);
    }

    if (filters.status) {
      filteredItems = filteredItems.filter(item => item.status === filters.status);
    }

    if (filters.brandCode) {
      filteredItems = filteredItems.filter(item => 
        item.brandCode?.toLowerCase().includes(filters.brandCode!.toLowerCase())
      );
    }

    if (filters.brandName) {
      filteredItems = filteredItems.filter(item => 
        item.brandName?.toLowerCase().includes(filters.brandName!.toLowerCase())
      );
    }

    if (filters.strategicFlag !== undefined) {
      filteredItems = filteredItems.filter(item => item.strategicFlag === filters.strategicFlag);
    }

    if (filters.displayType) {
      filteredItems = filteredItems.filter(item => item.displayType === filters.displayType);
    }

    if (filters.firstReceivedTime !== undefined) {
      filteredItems = filteredItems.filter(item => item.firstReceivedTime === filters.firstReceivedTime);
    }

    if (filters.selfBrandFlag !== undefined) {
      filteredItems = filteredItems.filter(item => item.selfBrandFlag === filters.selfBrandFlag);
    }

    if (filters.group) {
      filteredItems = filteredItems.filter(item => 
        item.group?.toLowerCase().includes(filters.group!.toLowerCase())
      );
    }

    if (filters.packaging) {
      filteredItems = filteredItems.filter(item => 
        item.packaging?.toLowerCase().includes(filters.packaging!.toLowerCase())
      );
    }
    
    if (filters.hasEdit !== undefined) {
      filteredItems = filteredItems.filter(item => item.hasEdit === filters.hasEdit);
    }

    // 全局搜索
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      filteredItems = filteredItems.filter(item => 
        item.code.toLowerCase().includes(searchTerm) ||
        item.name.toLowerCase().includes(searchTerm) ||
        item.englishName?.toLowerCase().includes(searchTerm) ||
        item.abbr?.toLowerCase().includes(searchTerm) ||
        item.brandName?.toLowerCase().includes(searchTerm)
      );
    }

    // 排序
    if (pagination.sortBy && pagination.sortOrder) {
      filteredItems.sort((a, b) => {
        const aValue = a[pagination.sortBy as keyof ItemMasterData];
        const bValue = b[pagination.sortBy as keyof ItemMasterData];
        
        if (aValue < bValue) {
          return pagination.sortOrder === 'ascend' ? -1 : 1;
        }
        if (aValue > bValue) {
          return pagination.sortOrder === 'ascend' ? 1 : -1;
        }
        return 0;
      });
    }

    // 分页
    const startIndex = (pagination.currentPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const pageData = filteredItems.slice(startIndex, endIndex);

    return {
      traceId: 'mock-trace-' + Date.now(),
      success: true,
      msg: null,
      errorCode: null,
      errorMessage: null,
      flag: 'success',
      data: {
        tableData: pageData,
        paging: {
          totalCount: filteredItems.length,
          currentPage: pagination.currentPage,
          pageSize: pagination.pageSize,
          totalPages: Math.ceil(filteredItems.length / pagination.pageSize)
        }
      }
    };
  }

  /**
   * Mock: 获取货品主数据详情
   */
  private async getMockItemMasterDataDetail(itemCode: string): Promise<ApiResponse<ItemMasterData>> {
    await delay(500);

    const item = itemMasterDataList.find(item => item.code === itemCode);
    
    if (item) {
      return {
        traceId: 'mock-trace-detail-' + Date.now(),
        success: true,
        msg: null,
        errorCode: null,
        errorMessage: null,
        flag: 'success',
        data: item
      };
    } else {
      return {
        traceId: 'mock-trace-detail-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'NOT_FOUND',
        errorMessage: '未找到指定的货品主数据',
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * Mock: 获取品牌选项列表
   */
  private async getMockBrandOptions(): Promise<ApiResponse<BrandOption[]>> {
    await delay(300);

    const brands: BrandOption[] = [
      { code: 'COCA_COLA', name: '可口可乐' },
      { code: 'PEPSI', name: '百事可乐' },
      { code: 'NESTLE', name: '雀巢' },
      { code: 'UNILEVER', name: '联合利华' },
      { code: 'PG', name: '宝洁' },
      { code: 'LOREAL', name: '欧莱雅' },
      { code: 'JOHNSON', name: '强生' },
      { code: 'COLGATE', name: '高露洁' },
      { code: 'KRAFT', name: '卡夫' },
      { code: 'DANONE', name: '达能' }
    ];

    return {
      traceId: 'mock-trace-brands-' + Date.now(),
      success: true,
      msg: null,
      errorCode: null,
      errorMessage: null,
      flag: 'success',
      data: brands
    };
  }

  /**
   * Mock: 获取类目选项列表
   */
  private async getMockCategoryOptions(level?: number, parentId?: string): Promise<ApiResponse<CategoryOption[]>> {
    await delay(300);

    const categories: CategoryOption[] = [
      // 一级类目
      { id: 'CAT1_001', name: '食品饮料', level: 1 },
      { id: 'CAT1_002', name: '个护用品', level: 1 },
      { id: 'CAT1_003', name: '家居用品', level: 1 },
      { id: 'CAT1_004', name: '电子产品', level: 1 },
      
      // 二级类目 - 食品饮料
      { id: 'CAT2_001', name: '饮料', level: 2, parentId: 'CAT1_001' },
      { id: 'CAT2_002', name: '零食', level: 2, parentId: 'CAT1_001' },
      { id: 'CAT2_003', name: '乳制品', level: 2, parentId: 'CAT1_001' },
      { id: 'CAT2_004', name: '调料', level: 2, parentId: 'CAT1_001' },
      
      // 二级类目 - 个护用品
      { id: 'CAT2_005', name: '洗护用品', level: 2, parentId: 'CAT1_002' },
      { id: 'CAT2_006', name: '口腔护理', level: 2, parentId: 'CAT1_002' },
      { id: 'CAT2_007', name: '护肤用品', level: 2, parentId: 'CAT1_002' },
      
      // 三级类目 - 饮料
      { id: 'CAT3_001', name: '碳酸饮料', level: 3, parentId: 'CAT2_001' },
      { id: 'CAT3_002', name: '果汁饮料', level: 3, parentId: 'CAT2_001' },
      { id: 'CAT3_003', name: '茶饮料', level: 3, parentId: 'CAT2_001' },
      { id: 'CAT3_004', name: '咖啡饮料', level: 3, parentId: 'CAT2_001' }
    ];

    let filteredCategories = categories;

    if (level !== undefined) {
      filteredCategories = filteredCategories.filter(cat => cat.level === level);
    }

    if (parentId) {
      filteredCategories = filteredCategories.filter(cat => cat.parentId === parentId);
    }

    return {
      traceId: 'mock-trace-categories-' + Date.now(),
      success: true,
      msg: null,
      errorCode: null,
      errorMessage: null,
      flag: 'success',
      data: filteredCategories
    };
  }

  /**
   * Mock: 导出货品主数据
   */
  private async getMockExportItemMasterData(
    filters: ItemMasterDataFilters,
    format: 'excel' | 'csv',
    scope: 'current' | 'all' | 'selected',
    selectedIds?: string[]
  ): Promise<ApiResponse<string>> {
    await delay(2000);

    // 模拟导出成功
    return {
      traceId: 'mock-trace-export-' + Date.now(),
      success: true,
      msg: null,
      errorCode: null,
      errorMessage: null,
      flag: 'success',
      data: `货品主数据导出成功，格式：${format}，范围：${scope}`
    };
  }

  /**
   * Mock: 更新货品主数据
   */
  private async getMockUpdateItemMasterData(item: ItemMasterData): Promise<ApiResponse<boolean>> {
    await delay(800);

    // 模拟更新货品主数据
    const index = itemMasterDataList.findIndex(i => i.code === item.code);
    
    if (index !== -1) {
      // 更新Mock数据
      itemMasterDataList[index] = {
        ...itemMasterDataList[index],
        ...item,
        gmtModified: Date.now(),
        modifierAccount: 'current_user'
      };
      
      return {
        traceId: 'mock-trace-update-' + Date.now(),
        success: true,
        msg: null,
        errorCode: null,
        errorMessage: null,
        flag: 'success',
        data: true
      };
    } else {
      return {
        traceId: 'mock-trace-update-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'NOT_FOUND',
        errorMessage: '未找到指定的商品',
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * Mock: 保存商品陈列参数配置
   */
  private async getMockSaveDisplayConfig(itemCode: string, updatedItem: ItemMasterData): Promise<ApiResponse<boolean>> {
    await delay(800);

    // 模拟保存陈列参数配置
    const index = itemMasterDataList.findIndex(item => item.code === itemCode);
    
    if (index !== -1) {
      // 更新Mock数据中的陈列配置字段
      itemMasterDataList[index] = {
        ...itemMasterDataList[index],
        ...updatedItem,
        displayConfigGmtModified: Date.now(),
        displayConfigModifierAccount: 'current_user'
      };
      
      return {
        traceId: 'mock-trace-save-config-' + Date.now(),
        success: true,
        msg: null,
        errorCode: null,
        errorMessage: null,
        flag: 'success',
        data: true
      };
    } else {
      return {
        traceId: 'mock-trace-save-config-' + Date.now(),
        success: false,
        msg: null,
        errorCode: 'NOT_FOUND',
        errorMessage: '未找到指定的商品',
        flag: 'fail',
        data: null
      };
    }
  }

  /**
   * Mock: 导入陈列参数配置
   */
  private async getMockImportDisplayConfig(file: File): Promise<ApiResponse<any>> {
    await delay(2000); // 模拟上传和处理时间

    // 模拟导入结果
    const importResult = {
      totalCount: 100,
      successCount: 95,
      failureCount: 5,
      failureItems: [
        { itemCode: 'ITEM001', reason: '商品不存在' },
        { itemCode: 'ITEM002', reason: '数据格式错误' },
        { itemCode: 'ITEM003', reason: '必填字段缺失' },
        { itemCode: 'ITEM004', reason: '数值超出范围' },
        { itemCode: 'ITEM005', reason: '重复数据' }
      ]
    };

    return {
      traceId: 'mock-trace-import-' + Date.now(),
      success: true,
      msg: null,
      errorCode: null,
      errorMessage: null,
      flag: 'success',
      data: importResult
    };
  }
}

// 导出服务实例
export const itemMasterDataService = new ItemMasterDataService();
export default itemMasterDataService;